import { Link, useNavigate, useParams } from "@tanstack/react-router";
import { ArrowLeft, Check, FileUp, RotateCcw, Trash2 } from "lucide-react";
import { useEffect, useRef, useState } from "react";
import {
  type Item,
  importOllieReport,
  type Location,
  type StockImport,
  type StockImportCommit,
  send,
  useChange,
  useItems,
  useLocations,
  usePackLists,
  useStockImport,
  useStockImports,
} from "../api";
import { sendAutosave, useAutosave } from "../autosave";
import {
  Dialog,
  DialogForm,
  ErrorNote,
  Toolbar,
  ToolbarButton,
  ToolbarSeparator,
  usePageTitle,
} from "../components/ui";
import { showDate, showDateTime } from "../format";
import { type PackChoice, usablePack } from "../forms/PackPicker";
import { useNotify } from "../notify";
import { SaveState } from "./Brews";
import { ImportGroup } from "./StockImportGroup";
import {
  categoryFor,
  defaultDecision,
  type Group,
  type GroupChoice,
  guessPack,
  itemFor,
  localKeyOf,
  lotsFor,
  type PackGuess,
  packKey,
  type ReportType,
  type ReviewState,
  startingState,
} from "./stockImportState";

const TYPE_ORDER: ReportType[] = ["Fermentable", "Hop", "Miscellaneous", "Packaging Material"];
const TYPE_LABELS: Partial<Record<ReportType, string>> = {
  Fermentable: "Fermentables",
  Hop: "Hops",
  Miscellaneous: "Miscellaneous",
  "Packaging Material": "Packaging",
};
export function OllieImportSection() {
  const { data: imports = [] } = useStockImports();
  const input = useRef<HTMLInputElement>(null);
  const navigate = useNavigate();
  const read = useChange(importOllieReport, {
    onSuccess: (imported) =>
      navigate({ to: "/settings/ollie/$importId", params: { importId: imported.id } }),
  });
  return (
    <section id="import-from-ollie" className="section">
      <div className="section-header" style={{ cursor: "default" }}>
        <h2>Import from Ollie</h2>
      </div>
      <div className="section-body stack">
        <p className="muted" style={{ margin: 0 }}>
          Bring in what Ollie Ops has on hand. In Ollie, export the Inventory On Hand Report as a
          CSV file and choose it here. You check what each of its items is in this app before
          anything is added; then its lots come in as opening stock, all as one entry that you can
          undo.
        </p>
        <div className="row">
          <button
            type="button"
            className="button"
            disabled={read.isPending}
            onClick={() => input.current?.click()}
          >
            <FileUp size={16} /> {read.isPending ? "Reading" : "Choose Ollie's report"}
          </button>
        </div>
        <ErrorNote error={read.error} />
        {imports.length ? (
          <div className="list-rows">
            {imports.map((i) => (
              <div key={i.id}>
                <Link to="/settings/ollie/$importId" params={{ importId: i.id }}>
                  {i.reportDate ? `Ollie report of ${showDate(i.reportDate)}` : i.filename}
                </Link>
                <span className="muted">
                  {i.status === "imported"
                    ? `Imported ${showDate(i.importedAt)}`
                    : i.status === "undone"
                      ? "Undone"
                      : `Not imported yet, ${i.lots} lots`}
                </span>
              </div>
            ))}
          </div>
        ) : null}
        <input
          ref={input}
          type="file"
          accept=".csv,text/csv"
          hidden
          onChange={(e) => {
            const file = e.target.files?.[0];
            if (file) read.mutate(file);
            e.target.value = "";
          }}
        />
      </div>
    </section>
  );
}
export function StockImportPage() {
  usePageTitle("Import from Ollie", "Settings");
  const { importId } = useParams({ from: "/settings/ollie/$importId" });
  const { data: report, isLoading, error } = useStockImport(importId);
  const { data: items, isLoading: loadingItems } = useItems();
  const { data: locations, isLoading: loadingPlaces } = useLocations();
  if (isLoading || loadingItems || loadingPlaces) {
    return <div className="page-body muted">Loading</div>;
  }
  if (error || !report || !items || !locations) {
    return (
      <div className="page-body">
        <ErrorNote error={error ?? new Error("Report not found")} />
      </div>
    );
  }
  if (report.status !== "draft") return <ImportedReport report={report} />;
  return (
    <Review
      key={report.id}
      report={report}
      items={items}
      locations={locations.filter((l) => !l.archived)}
    />
  );
}
function Review({
  report,
  items,
  locations,
}: {
  report: StockImport;
  items: Item[];
  locations: Location[];
}) {
  const [start] = useState(() => startingState(report, items, locations));
  const [state, setState] = useState<ReviewState>(start.state);
  const [confirming, setConfirming] = useState<"import" | "discard" | null>(null);
  const notify = useNotify();
  const navigate = useNavigate();

  const autosave = useAutosave<ReviewState>({
    localKey: localKeyOf(report.id),
    save: ({ value, savedAt }, keepalive) =>
      sendAutosave(
        "PUT",
        `/stock-imports/${report.id}/state`,
        { state: value, savedAt },
        keepalive,
      ),
  });
  const sent = useRef<ReviewState | null>(start.unsent ? null : start.state);
  useEffect(() => {
    if (state === sent.current) return;
    sent.current = state;
    autosave.update(state);
  }, [state, autosave.update]);

  const setGroup = (key: string, choice: GroupChoice) =>
    setState((s) => ({ ...s, groups: { ...s.groups, [key]: choice } }));
  const choiceOf = (g: Group): GroupChoice =>
    state.groups[g.key] ?? {
      decision: defaultDecision(g),
      lots: lotsFor(g, defaultDecision(g), items),
    };
  const categories = new Map(report.groups.map((g) => [g.key, categoryFor(g, choiceOf(g), items)]));
  const packLists = usePackLists(
    report.groups.map((g) => ({ dimension: g.dimension, category: categories.get(g.key) ?? null })),
  );
  const guessOf = (g: Group): PackGuess =>
    guessPack(
      g,
      itemFor(g, choiceOf(g).decision, items, choiceOf(g).picked),
      packLists.get(packKey(g, categories.get(g.key) ?? null)) ?? [],
    );
  const packOf = (g: Group): PackChoice => {
    const chosen = choiceOf(g).pack;
    return chosen === undefined ? guessOf(g).pack : chosen;
  };
  const placeOf = (g: Group, index: number) =>
    state.groups[g.key]?.lots[String(index)]?.locationId ?? state.typeLocations[g.type] ?? "";

  const undecided = report.groups.filter((g) => choiceOf(g).decision.kind === "undecided");
  const body: StockImportCommit = {
    groups: report.groups.flatMap((g) => {
      const choice = state.groups[g.key];
      const decision = choice?.decision ?? defaultDecision(g);
      if (decision.kind === "undecided") return [];
      return {
        key: g.key,
        decision,
        pack: decision.kind === "skip" ? null : usablePack(packOf(g)),
        lots:
          decision.kind === "skip"
            ? []
            : g.lots
                .filter((l) => choice?.lots[String(l.index)]?.include ?? true)
                .map((l) => {
                  const lot = choice?.lots[String(l.index)];
                  return {
                    index: l.index,
                    locationId: placeOf(g, l.index),
                    ...(lot?.region?.trim() ? { region: lot.region.trim() } : {}),
                    ...(lot?.grower?.trim() ? { grower: lot.grower.trim() } : {}),
                  };
                }),
      };
    }),
  };
  const ticked = body.groups.reduce((n, g) => n + g.lots.length, 0);
  const unplaced = body.groups.some((g) => g.lots.some((l) => !l.locationId));

  const commit = useChange(
    async (_: undefined, key) => {
      await autosave.settle();
      return send.commitStockImport({ id: report.id, body }, key);
    },
    {
      onSuccess: (done) => {
        setConfirming(null);
        notify({
          title: `${done.lots} lots brought in from Ollie`,
          link: { kind: "stock_import", importId: report.id },
        });
        window.scrollTo({ top: 0 });
      },
    },
  );
  const discard = useChange(
    async (_: undefined, key) => {
      await autosave.settle();
      return send.discardStockImport(report.id, key);
    },
    { onSuccess: () => navigate({ to: "/settings" }) },
  );

  const byType = TYPE_ORDER.map((t) => ({
    type: t,
    groups: report.groups.filter((g) => g.type === t),
  })).filter((s) => s.groups.length);

  return (
    <>
      <Toolbar
        left={
          <>
            <Link to="/settings" className="toolbar-button" title="Back to settings">
              <ArrowLeft size={20} />
              <span className="label">Settings</span>
            </Link>
            <ToolbarSeparator />
          </>
        }
        right={
          <ToolbarButton
            icon={<Trash2 size={20} />}
            label="Throw away"
            title="Throw this report away without importing it"
            onClick={() => setConfirming("discard")}
          />
        }
      />
      <div className="page-body centered stock-import">
        <h1 className="page-title">
          {report.reportDate ? `Ollie report of ${showDate(report.reportDate)}` : report.filename}
        </h1>
        <p className="muted import-lede">
          {report.lots} lots of {report.groups.length} items. Say what each item is here, and which
          lots come in where; nothing is added until you click <strong>Import</strong>, and Import
          waits until every item is matched, made a new item, or left out.
        </p>
        {report.wip.length ? (
          <p className="notice">
            <strong>Not brought in:</strong> {report.wip.reduce((n, w) => n + w.lots, 0)} lots of
            beer in tanks and barrels ({report.wip.map((w) => w.name).join(", ")}). Beer in progress
            belongs with cellar work, which comes later.
          </p>
        ) : null}
        {report.problems.length ? (
          <div className="form-error">
            Rows that could not be read:{" "}
            {report.problems.map((p) => `line ${p.line}, ${p.reason}`).join("; ")}.
          </div>
        ) : null}

        <section className="import-places">
          <h2>Where each kind goes</h2>
          <div className="import-place-grid">
            {byType.map(({ type }) => (
              <label key={type} className="field">
                <span>{TYPE_LABELS[type]}</span>
                <select
                  className="select"
                  value={state.typeLocations[type] ?? ""}
                  onChange={(e) =>
                    setState((s) => ({
                      ...s,
                      typeLocations: { ...s.typeLocations, [type]: e.target.value },
                    }))
                  }
                >
                  {locations.map((l) => (
                    <option key={l.id} value={l.id}>
                      {l.name}
                    </option>
                  ))}
                </select>
              </label>
            ))}
          </div>
        </section>

        {byType.map(({ type, groups }) => (
          <section key={type} className="import-type">
            <h2>
              {TYPE_LABELS[type]} <span className="count">{groups.length}</span>
            </h2>
            {groups.map((g) => (
              <ImportGroup
                key={g.key}
                group={g}
                choice={choiceOf(g)}
                category={categories.get(g.key) ?? null}
                guess={guessOf(g)}
                items={items}
                locations={locations}
                placeOf={(index) => placeOf(g, index)}
                onChange={(choice) => setGroup(g.key, choice)}
              />
            ))}
          </section>
        ))}
        <ErrorNote error={commit.error} />
      </div>

      <div className="brew-actions">
        <SaveState status={autosave.status} idle="Your choices save as you go" />
        {undecided.length ? (
          <button
            type="button"
            className="button"
            title="Go to the next item to say what it is"
            onClick={() =>
              document
                .querySelector(".import-group.undecided")
                ?.scrollIntoView({ behavior: "smooth", block: "center" })
            }
          >
            {undecided.length === 1 ? "1 item" : `${undecided.length} items`} still to decide
          </button>
        ) : null}
        <button
          type="button"
          className="button primary"
          disabled={ticked === 0 || unplaced || undecided.length > 0 || commit.isPending}
          onClick={() => setConfirming("import")}
        >
          <Check size={18} /> Import {ticked} lots
        </button>
      </div>

      <Dialog
        open={confirming === "import"}
        onClose={() => setConfirming(null)}
        title="Import these lots?"
      >
        <DialogForm
          onSubmit={() => commit.mutate(undefined)}
          footer={
            <>
              <button type="button" className="button" onClick={() => setConfirming(null)}>
                Cancel
              </button>
              <button type="submit" className="button primary" disabled={commit.isPending}>
                Import
              </button>
            </>
          }
        >
          <p>
            {ticked} lots come into inventory as opening stock, each with Ollie's lot code, date and
            price, all as one entry. Undo, on this page afterwards, takes them all back out.
          </p>
          <ErrorNote error={commit.error} />
        </DialogForm>
      </Dialog>
      <Dialog
        open={confirming === "discard"}
        onClose={() => setConfirming(null)}
        title="Throw this report away?"
      >
        <DialogForm
          onSubmit={() => discard.mutate(undefined)}
          footer={
            <>
              <button type="button" className="button" onClick={() => setConfirming(null)}>
                Keep it
              </button>
              <button type="submit" className="button danger" disabled={discard.isPending}>
                Throw away
              </button>
            </>
          }
        >
          <p>
            Nothing from it is in inventory, so nothing there changes. You can choose the report
            again.
          </p>
          <ErrorNote error={discard.error} />
        </DialogForm>
      </Dialog>
    </>
  );
}
/** A report already brought in: what each of its items went in as, and Undo. */
function ImportedReport({ report }: { report: StockImport }) {
  const [confirming, setConfirming] = useState<"undo" | "discard" | null>(null);
  const notify = useNotify();
  const navigate = useNavigate();
  const undo = useChange((_: undefined, key) => send.undoStockImport(report.id, key), {
    onSuccess: () => {
      setConfirming(null);
      notify("The Ollie import is undone: its lots are back out of inventory");
    },
  });
  const discard = useChange((_: undefined, key) => send.discardStockImport(report.id, key), {
    onSuccess: () => navigate({ to: "/settings" }),
  });
  const resolved = Object.entries(report.resolved ?? {});
  return (
    <>
      <Toolbar
        left={
          <Link to="/settings" className="toolbar-button" title="Back to settings">
            <ArrowLeft size={20} />
            <span className="label">Settings</span>
          </Link>
        }
        right={
          report.status === "imported" ? (
            <ToolbarButton
              icon={<RotateCcw size={20} />}
              label="Undo import"
              onClick={() => setConfirming("undo")}
            />
          ) : (
            <ToolbarButton
              icon={<Trash2 size={20} />}
              label="Throw away"
              onClick={() => setConfirming("discard")}
            />
          )
        }
      />
      <div className="page-body centered stock-import">
        <h1 className="page-title">
          {report.reportDate ? `Ollie report of ${showDate(report.reportDate)}` : report.filename}
        </h1>
        <p className="notice">
          {report.status === "imported"
            ? `Imported ${report.importedAt ? showDateTime(report.importedAt) : ""}. Its lots are in inventory as opening stock.`
            : "This import was undone: its lots were taken back out of inventory."}
        </p>
        {resolved.length ? (
          <section className="import-type">
            <h2>What each item went in as</h2>
            <div className="list-rows">
              {resolved.map(([key, r]) => (
                <div key={key}>
                  <span>{key.split("|")[1]}</span>
                  <Link to="/items/$itemId" params={{ itemId: r.itemId }}>
                    {r.name}
                  </Link>
                </div>
              ))}
            </div>
          </section>
        ) : null}
      </div>
      <Dialog
        open={confirming === "undo"}
        onClose={() => setConfirming(null)}
        title="Undo this import?"
      >
        <DialogForm
          onSubmit={() => undo.mutate(undefined)}
          footer={
            <>
              <button type="button" className="button" onClick={() => setConfirming(null)}>
                Keep it
              </button>
              <button type="submit" className="button primary" disabled={undo.isPending}>
                Undo import
              </button>
            </>
          }
        >
          <p>
            Every lot it brought in is taken back out of inventory, as one entry. Items it made
            stay, with nothing on hand.
          </p>
          <ErrorNote error={undo.error} />
        </DialogForm>
      </Dialog>
      <Dialog
        open={confirming === "discard"}
        onClose={() => setConfirming(null)}
        title="Throw this report away?"
      >
        <DialogForm
          onSubmit={() => discard.mutate(undefined)}
          footer={
            <>
              <button type="button" className="button" onClick={() => setConfirming(null)}>
                Keep it
              </button>
              <button type="submit" className="button danger" disabled={discard.isPending}>
                Throw away
              </button>
            </>
          }
        >
          <p>Nothing from it is in inventory now, so nothing there changes.</p>
          <ErrorNote error={discard.error} />
        </DialogForm>
      </Dialog>
    </>
  );
}
